/**
 * Point layer factory for dense environmental data (fires, earthquakes).
 * Fires: ScatterplotLayer with FRP-based sizing (no HeatmapLayer — matches Worldmonitor).
 * Earthquakes: per-magnitude ScatterplotLayer at every zoom (matches Worldmonitor).
 */

import { ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent } from '@/sources/base-source';
import { getZoomAlpha } from './point-layer';

/** Extract fire radiative power (MW) from event metadata — drives radius. */
function getFireFRP(event: NormalizedEvent): number {
  const raw = event.raw as Record<string, unknown> | undefined;
  return typeof raw?.frp === 'number' ? raw.frp : 0;
}

/** Extract brightness temperature (Kelvin) from event metadata — drives color. */
function getFireBrightness(event: NormalizedEvent): number {
  const raw = event.raw as Record<string, unknown> | undefined;
  return typeof raw?.brightness === 'number' ? raw.brightness : 0;
}

/**
 * Color fires by brightness temperature (WM DeckGLMap.ts:3361-3365).
 * Size and color are two independent attributes: radius encodes radiative
 * power, color encodes brightness — feeding frp into this table lands every
 * fire in the low tier because frp is tens of MW, not hundreds of Kelvin.
 */
export function getFireColor(event: NormalizedEvent): [number, number, number, number] {
  const brightness = getFireBrightness(event);
  if (brightness > 400) return [255, 30, 0, 220];
  if (brightness > 350) return [255, 140, 0, 200];
  return [255, 220, 50, 180];
}

/** Extract a numeric weight from earthquake event metadata. */
function getQuakeWeight(event: NormalizedEvent): number {
  const raw = event.raw as Record<string, unknown> | undefined;
  const mag = raw?.magnitude ?? raw?.mag;
  if (typeof mag === 'number') return Math.max(0.5, mag);
  return 1;
}

/**
 * Earthquake color by magnitude tier matching WorldMonitor exactly:
 * M6+ → red (#ff2020), M4+ → orange (#ff8800), else → yellow (#ffdd00).
 */
function getQuakeColor(event: NormalizedEvent): [number, number, number, number] {
  const mag = getQuakeWeight(event);
  if (mag >= 6) return [255, 0, 0, 200];
  if (mag >= 5) return [255, 100, 0, 200];
  return [255, 100, 50, 200];
}

/**
 * Create layers for fire hotspot data.
 * ScatterplotLayer only (no HeatmapLayer) — FRP-based sizing and brightness coloring.
 */
export function createFireLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  const opacity = getZoomAlpha(zoom);

  return [new ScatterplotLayer<NormalizedEvent>({
    id: 'points-fires',
    data: events,
    pickable: true,
    filled: true,
    radiusMinPixels: 3,
    radiusMaxPixels: 12,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: (d) => getFireColor(d),
    getRadius: (d) => {
      const frp = getFireFRP(d);
      return Math.min(frp * 200, 30000) || 5000;
    },
    opacity,
  })];
}

/**
 * Create layers for earthquake data — per-magnitude scatter dots at every
 * zoom, like WM. (The old HeatmapLayer crossfade REPLACED the magnitude
 * dots below zoom 3 with a green-ended density wash that read as "safe";
 * removed by decision 2026-09-01.)
 */
export function createEarthquakeLayers(
  events: NormalizedEvent[],
  _zoom: number,
): Layer[] {
  return [new ScatterplotLayer<NormalizedEvent>({
    id: 'points-earthquakes',
    data: events,
    pickable: true,
    filled: true,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: (d) => getQuakeColor(d),
    getRadius: (d) => Math.pow(2, getQuakeWeight(d)) * 1000,
    radiusMinPixels: 4,
    radiusMaxPixels: 30,
  })];
}
